import { useCallback, useMemo } from 'react'
import useAirlineFilterManager from '../airlineFilterManager'
import useFlightTimingFilterManager from '../flightTimingFilterManager'
import useIncludeAirportsManager from '../includeAirportsManager'
import usePriceRangeFilterManager from '../priceRangeFilterManager'
import useRecommendedFilterManager from '../recommendedFilterManager'
import useStopFilterManager from '../stopFilterManager'

export const useClearFilters = (journeyLeg: 0 | 1 = 0) => {
    const { shouldShowReset: stopFilterApplied, stopReset, title: stopFilterTitle } = useStopFilterManager(journeyLeg)
    const {
        shouldShowReset: airlineFilterApplied,
        handleReset: airlineReset,
        title: airlineFilterTitle
    } = useAirlineFilterManager(journeyLeg)

    const {
        shouldShowReset: flightTimingFilterApplied,
        handleReset: flightTimingReset,
        title: flightTimingFilterTitle
    } = useFlightTimingFilterManager(journeyLeg)

    const {
        shouldShowReset: arrivalTimingFilterApplied,
        handleReset: arrivalTimingReset,
        title: arrivalTimingFilterTitle
    } = useFlightTimingFilterManager(journeyLeg, 'arrival')

    const {
        title: priceRangeFilterTitle,
        isPriceRangeFilterApplied,
        handleReset: priceRangeReset
    } = usePriceRangeFilterManager(journeyLeg)

    const { recommendedFilterOptions } = useRecommendedFilterManager(journeyLeg)

    const {
        shouldShowReset: includeAirportsFilterApplied,
        handleReset: includeAirportsReset,
        title: includeAirportsFilterTitle
    } = useIncludeAirportsManager(journeyLeg)
    const appliedFilterList = useMemo(() => {
        return [
            {
                show: stopFilterApplied,
                label: stopFilterTitle,
                onDelete: stopReset
            },
            {
                show: airlineFilterApplied,
                label: airlineFilterTitle,
                onDelete: airlineReset
            },
            {
                show: flightTimingFilterApplied,
                label: flightTimingFilterTitle,
                onDelete: flightTimingReset
            },
            {
                show: arrivalTimingFilterApplied,
                label: arrivalTimingFilterTitle,
                onDelete: arrivalTimingReset
            },
            {
                show: isPriceRangeFilterApplied,
                label: priceRangeFilterTitle,
                onDelete: priceRangeReset
            },
            {
                show: includeAirportsFilterApplied,
                label: includeAirportsFilterTitle,
                onDelete: includeAirportsReset
            },
            ...recommendedFilterOptions.map(option => ({
                show: option.checked && option.show && option.showInAppliedFilterList,
                label: option.label,
                onDelete: option.onClick
            }))
        ]
    }, [
        stopFilterApplied,
        airlineFilterApplied,
        flightTimingFilterApplied,
        stopReset,
        airlineReset,
        flightTimingReset,
        arrivalTimingFilterApplied,
        arrivalTimingReset,
        arrivalTimingFilterTitle,
        isPriceRangeFilterApplied,
        priceRangeFilterTitle,
        priceRangeReset,
        recommendedFilterOptions,
        includeAirportsFilterApplied,
        includeAirportsFilterTitle,
        includeAirportsReset
    ])

    const handleClearAllFilters = useCallback(() => {
        appliedFilterList.forEach(filter => filter.show && filter.onDelete())
    }, [appliedFilterList])

    const numberOfFiltersApplied = useMemo(() => {
        return appliedFilterList.filter(filter => filter.show).length
    }, [appliedFilterList])

    const isFilterApplied = useMemo(() => {
        return numberOfFiltersApplied > 0
    }, [numberOfFiltersApplied])

    return {
        handleClearAllFilters,
        appliedFilterList,
        isFilterApplied,
        numberOfFiltersApplied
    }
}
